import { useMemo, useState } from 'react'
import { magasin } from '../lib/client/store.ts'
import { useMagasin, type Produit, type Article } from '../lib/client/hooks.ts'
import { Sheet } from '../components/Sheet.tsx'
import { Coche } from '../components/Icone.tsx'
import { EditeurRecette } from './EditeurRecette.tsx'
import { normalise } from '../lib/search.ts'

type Recette = {
  id: string
  title: string
  servings: number | null
  prepMinutes: number | null
  cookMinutes: number | null
  steps: string[]
  sourceUrl: string | null
  tags: string[]
}
type Ingredient = {
  id: string
  recipeId: string
  catalogItemId: string | null
  freeText: string | null
  quantityText: string | null
  optional: boolean
  sortOrder: number
}

/**
 * Le verdict « ai-je tout ? » est le deuxième élément le plus proéminent de
 * la carte, après le titre.
 *
 * INVERSION VOLONTAIRE : « il manque 3 » est signalé plus fortement que
 * « il manque 7 ». Une recette dont on est loin n'est pas un problème, c'est
 * juste pas ce soir. La couleur suit l'ACTIONNABILITÉ, pas le déficit.
 */
function verdict(manquants: number) {
  if (manquants === 0) return { texte: 'on peut la faire', classe: 'bg-ok-soft text-ok' }
  if (manquants <= 3)
    return { texte: `il manque ${manquants}`, classe: 'bg-bientot-soft text-bientot-ink' }
  return { texte: `il manque ${manquants}`, classe: 'bg-surface-sunk text-muted' }
}

export function useRecettes() {
  const { etat } = useMagasin()
  return useMemo(() => {
    const vivant = <T,>(m: Map<string, unknown>) =>
      [...m.values()].filter((l) => !(l as { deletedAt?: unknown }).deletedAt) as T[]

    const produits = new Map(vivant<Produit>(etat.catalogItems).map((p) => [p.id, p]))
    const enStock = new Set(
      vivant<Article>(etat.inventoryItems)
        .filter((a) => a.state !== 'epuise')
        .map((a) => a.catalogItemId),
    )
    const ingredients = vivant<Ingredient>(etat.recipeIngredients)

    const recettes = vivant<Recette>(etat.recipes).map((r) => {
      const siens = ingredients
        .filter((i) => i.recipeId === r.id)
        .sort((a, b) => a.sortOrder - b.sortOrder)
      const manquants = siens.filter((i) => {
        // `optional` est ce qui rend le verdict utilisable : sans lui, une
        // branche de persil ferait échouer un bœuf bourguignon complet.
        if (i.optional) return false
        if (i.catalogItemId) return !enStock.has(i.catalogItemId)
        // Texte libre : on tente un rapprochement par nom normalisé, sinon on
        // le compte comme manquant — mieux vaut annoncer un manque de trop
        // qu'une recette faisable qui ne l'est pas.
        const cible = normalise(i.freeText ?? '')
        return ![...enStock].some((id) => normalise(produits.get(id)?.name ?? '') === cible)
      })
      return { recette: r, ingredients: siens, manquants }
    })

    recettes.sort((a, b) => a.manquants.length - b.manquants.length || a.recette.title.localeCompare(b.recette.title, 'fr'))
    return { recettes, produits, enStock }
  }, [etat])
}

export function Recettes() {
  const { recettes, produits } = useRecettes()
  const [ouverte, setOuverte] = useState<string | null>(null)
  const [edition, setEdition] = useState<string | 'nouvelle' | null>(null)
  const detail = recettes.find((r) => r.recette.id === ouverte)

  return (
    <div className="pb-24">
      <div className="sticky top-0 z-20 bg-canvas/80 backdrop-blur-xl" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="flex h-[var(--size-bar)] items-center justify-between px-4">
          <h1 className="font-serif text-title text-ink">Recettes</h1>
          {/* décalé à gauche : la pastille de synchro et les réglages
              occupent déjà le coin haut-droit */}
          <button
            type="button"
            onClick={() => setEdition('nouvelle')}
            className="pressable mr-20 h-9 rounded-full bg-surface-sunk px-3 text-meta text-ink"
          >
            + Recette
          </button>
        </div>
      </div>

      <div className="px-4">
        {recettes.length === 0 && (
          <div className="mt-16 text-center">
            <p className="text-head text-ink">Aucune recette.</p>
            <p className="mt-1 text-meta text-muted">
              Écris-la toi-même, ou colle-en une à Claude : il la rangera ici.
            </p>
            <button
              type="button"
              onClick={() => setEdition('nouvelle')}
              className="pressable mt-4 h-11 rounded-sm bg-accent px-5 text-body text-accent-ink"
            >
              Écrire une recette
            </button>
          </div>
        )}

        <ul className="flex flex-col gap-2">
          {recettes.map(({ recette, manquants }) => {
            const v = verdict(manquants.length)
            return (
              <li key={recette.id}>
                <button
                  type="button"
                  onClick={() => setOuverte(recette.id)}
                  className="pressable w-full rounded-lg bg-surface p-4 text-left shadow-row"
                >
                  <span className="block font-serif text-title text-ink">{recette.title}</span>
                  <span className="mt-2 flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-meta ${v.classe}`}>{v.texte}</span>
                    {recette.prepMinutes != null && (
                      <span className="tnum text-meta text-muted">
                        {(recette.prepMinutes ?? 0) + (recette.cookMinutes ?? 0)} min
                      </span>
                    )}
                    {recette.servings != null && (
                      <span className="tnum text-meta text-muted">{recette.servings} pers.</span>
                    )}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </div>

      <Sheet ouverte={!!detail} onFermer={() => setOuverte(null)} titre={detail?.recette.title ?? ''}>
        {detail && (
          <>
            <button
              type="button"
              onClick={() => {
                const id = detail.recette.id
                setOuverte(null)
                setEdition(id)
              }}
              className="pressable mb-4 h-11 w-full rounded-sm bg-surface-sunk text-body text-ink"
            >
              Modifier la recette
            </button>

            <h3 className="mb-2 text-label uppercase text-muted">Ingrédients</h3>
            <ul className="mb-5">
              {detail.ingredients.map((i) => {
                const manque = detail.manquants.includes(i)
                const nom = i.catalogItemId ? (produits.get(i.catalogItemId)?.name ?? '?') : i.freeText
                return (
                  <li key={i.id} className="flex items-baseline justify-between gap-3 border-b border-line py-2.5">
                    <span className={manque ? 'text-ink' : 'text-muted'}>
                      {nom}
                      {i.optional && <span className="text-meta text-muted"> (facultatif)</span>}
                    </span>
                    <span className="flex shrink-0 items-baseline gap-2">
                      {i.quantityText && <span className="tnum text-meta text-muted">{i.quantityText}</span>}
                      {manque ? (
                        <button
                          type="button"
                          onClick={() =>
                            magasin.dispatch('list.add', {
                              id: magasin.id(),
                              catalogItemId: i.catalogItemId ?? undefined,
                              freeText: i.catalogItemId ? undefined : nom,
                              quantityText: i.quantityText ?? undefined,
                              reason: 'menu',
                              sourceRecipeId: detail.recette.id,
                            })
                          }
                          className="pressable text-meta text-accent"
                        >
                          + courses
                        </button>
                      ) : (
                        <span aria-label="en stock" className="text-ok"><Coche className="size-4" /></span>
                      )}
                    </span>
                  </li>
                )
              })}
            </ul>

            {detail.manquants.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  for (const i of detail.manquants) {
                    magasin.dispatch('list.add', {
                      id: magasin.id(),
                      catalogItemId: i.catalogItemId ?? undefined,
                      freeText: i.catalogItemId ? undefined : (i.freeText ?? ''),
                      quantityText: i.quantityText ?? undefined,
                      reason: 'menu',
                      sourceRecipeId: detail.recette.id,
                    })
                  }
                  setOuverte(null)
                }}
                className="pressable mb-5 h-11 w-full rounded-sm bg-accent text-body text-accent-ink"
              >
                Ajouter les {detail.manquants.length} manquants aux courses
              </button>
            )}

            {detail.recette.steps.length > 0 && (
              <>
                <h3 className="mb-2 text-label uppercase text-muted">Préparation</h3>
                <ol className="mb-4 flex flex-col gap-3">
                  {detail.recette.steps.map((e, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="tnum shrink-0 text-meta text-muted">{i + 1}</span>
                      {/* Texte venu du web : on ne l'interprète jamais, on
                          l'affiche tel quel. */}
                      <span className="text-body text-ink">{e}</span>
                    </li>
                  ))}
                </ol>
              </>
            )}
          </>
        )}
      </Sheet>

      <EditeurRecette recetteId={edition} onFermer={() => setEdition(null)} />
    </div>
  )
}
